/**
 * One headline figure - a rolled-up quantity for the part the page is about.
 *
 * A tile rather than a slice of a pie, and the reason is what the four numbers
 * ARE. Dry mass, wet mass and power draw share no scale and no whole: kilograms
 * and watts cannot be added, so there is no total for them to be parts of, and a
 * pie of them would be drawing angles out of quantities that have no ratio. Four
 * numbers in different units are four numbers; a row of tiles says that plainly,
 * and each tile has room for the caveat the number carries - which a slice does
 * not.
 *
 * The caveats are chips beside the value rather than a footnote, because they
 * change what the number MEANS. `141.85 kg` and `≥ 141.85 kg, partial` are
 * different claims, and a reader who takes the first for the second has been
 * misled by the screen rather than by the model.
 *
 * A BUDGET, where the part has one, goes below a rule rather than beside the
 * figure. Two claims about one number: what the model says it is, and what it
 * is allowed to be. They can disagree, and the tile has to let them - a total
 * that is a floor sits above a verdict that says the floor already exceeds the
 * budget, and both are true at once.
 */

import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import BudgetVerdict from './BudgetVerdict'
import type { PartTotal, RollingAttribute } from '../../schema'
import type { BudgetReading } from './budget-model'
import { formatTotal, unstatedSummary } from './budget-model'

export const MINIMUM_WHY =
  'A floor, not a figure: something below this was counted short — an unbounded ' +
  'multiplicity taken at its lower bound, or a part that states no value. The real ' +
  'figure is this or more.'

// The SAME two causes, read against a 'min'. A weakest link computed without one
// of the links can only look STRONGER than the assembly is, so the number is a
// ceiling - and calling it a floor would offer a margin that is not there.
export const MAXIMUM_WHY =
  'A ceiling, not a figure: this aggregates as a minimum — the weakest part — and ' +
  'something below it states no value. Whatever it turns out to be can only bring ' +
  'this number down.'

export const PARTIAL_WHY =
  'Partial: something below this states no value, and nothing was added for it. ' +
  'Unset is not zero, so the real total is this or more.'

export default function QuantityTile({
  attribute,
  total,
  budget = null,
}: {
  attribute: RollingAttribute
  /** The quantity, or null when this row carries none at all. */
  total: PartTotal | null
  /**
   * The verdict, where this part has a budget for this quantity.
   *
   * Null is `unbudgeted`, and it renders as NOTHING - no badge, no grey "n/a",
   * no empty slot. A part nobody has given a figure to has not passed anything,
   * and a placeholder beside four real verdicts is read as a fifth.
   */
  budget?: BudgetReading | null
}) {
  const unknown = !total || total.total === null
  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Typography variant="body2" color="text.secondary">
        {attribute.label}
      </Typography>
      <Typography
        variant="h5"
        // Proportional figures: tabular-nums gives every digit the width of a
        // zero, which makes a standalone number look loose at this size.
        sx={{ mt: 0.5, color: unknown ? 'text.disabled' : 'text.primary' }}
      >
        {formatTotal(total)}
      </Typography>
      {/* THE CAVEATS DESCRIBE A NUMBER, so they are suppressed when there is
          not one. 'not stated' beside 'a floor' reads as a figure somebody is
          withholding rather than one nothing below ever stated - and 'a floor'
          under nothing is not even wrong, it is meaningless. */}
      <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
        {!unknown && total?.bound === 'at_least' && (
          <Tooltip title={MINIMUM_WHY} describeChild>
            <Chip label="a floor" size="small" variant="outlined" />
          </Tooltip>
        )}
        {!unknown && total?.bound === 'at_most' && (
          <Tooltip title={MAXIMUM_WHY} describeChild>
            <Chip label="a ceiling" size="small" variant="outlined" />
          </Tooltip>
        )}
        {!unknown && total?.is_partial && (
          <Tooltip title={PARTIAL_WHY} describeChild>
            <Chip label="partial" size="small" variant="outlined" color="warning" />
          </Tooltip>
        )}
      </Stack>
      {total && total.unstated.length > 0 && (
        // The full list on hover, so nothing is lost - a model where nobody has
        // stated a mass anywhere names two dozen parts, and the tile has to stay
        // readable without the answer becoming unavailable.
        <Tooltip title={total.unstated.join(', ')} describeChild>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Nothing stated by: {unstatedSummary(total.unstated)}
          </Typography>
        </Tooltip>
      )}

      {/* The verdict sits UNDER the figure and behind a rule, because it is a
          second claim about the same number rather than a decoration on it: the
          figure is what the model says, the verdict is what it is allowed. */}
      {budget && (
        <Box sx={{ mt: 1.5 }}>
          <Divider sx={{ mb: 1 }} />
          <BudgetVerdict reading={budget} />
        </Box>
      )}
    </Paper>
  )
}
